import React, { createRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormWrapper, Title, ConfirmDialog } from '../../../common'
import { submitReclamation, loadCategories } from '../../../actions/reclamations.actions';
import { loadResidences } from '../../../actions/residences.actions';
import * as Sentry from '@sentry/react';
import icoQuestion from '../../../assets/icons/ico-question.svg';
import { find } from 'lodash'
import OwnerContactInformationSection from './OwnerContactInformationSection';
import ApartmentInformationSection from './ApartmentInformationSection';
import DefectInformationSection from './DefectInformationSection'
import SubmittingPage from '../SubmittingPage'
import { getTodaysDateString } from '../../../common/utils'
import { prepareAttachments } from '../../../common/attachments'
import { useHistory } from 'react-router-dom';
import { Form } from 'react-final-form';

const ReclamationPage = ({ match }) => {
  const history = useHistory()
  
  const { id: encodedResidenceIdFromURL } = match.params

  if (!encodedResidenceIdFromURL) {
    window.location.href = 'https://www.reklamacni-formular.cz';
  }

  const residenceBuffer = Buffer.from(encodedResidenceIdFromURL, 'base64');
  const residencePath = residenceBuffer.toString('ascii');
  const residenceId = residencePath.substring(residencePath.indexOf(":") + 1);

  const [dialog, setDialog] = useState(null)
  const [files, setFiles] = useState([])
  const [fileErrors, setFileErrors] = useState([])
  const [isPreparingFiles, setIsPreparingFiles] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [submitError, setSubmitError] = useState(null)
  const [sessionId] = useState(() => `reclamation_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)

  const dispatch = useDispatch()

  const residencesState = useSelector(state => state.residences)
  const reclamationsState = useSelector(state => state.reclamations)

  const {
    isLoadingResidences,
    isLoadingResidenceNames,
    isLoading: isLoadingResidencesState,
    residences
  } = residencesState
  const {
    isLoadingReclamations,
    isLoadingCategories,
    isLoading: isLoadingReclamationsState,
    categories
  } = reclamationsState

  const residenceNamesItems = residences.map(r => ({ id: r.id, name: r.name })) || []
  const categoriesItems = categories.map(n => ({ id: n.label, name: n.label })) || []

  const residence = find(residences, residence => String(residence.id) === residenceId)

  const isLoading =
    isLoadingReclamations ||
    isLoadingCategories ||
    isLoadingResidences ||
    isLoadingResidenceNames ||
    isLoadingResidencesState ||
    isLoadingReclamationsState

  const fileInput = createRef()

  useEffect(() => {
    dispatch(loadResidences());
    dispatch(loadCategories());

    // Set up session tracking for Sentry
    Sentry.setTag('session_id', sessionId);
    Sentry.setTag('residence_id', residenceId);

    // Add initial breadcrumb for page load
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: 'ReclamationPage loaded',
      level: 'info',
      data: {
        residence_id: residenceId,
        session_id: sessionId
      }
    });
  }, [dispatch, sessionId, residenceId])

  if (isLoading) return <div className='loader'>Načítám...</div>

  const handleSubmitForm = async (data) => {
    if (isSubmitting) return
    dialogReset()
    setSubmitError(null)
    setUploadProgress(0)
    setIsSubmitting(true)
    window.scrollTo(0, 0)

    const onUploadProgress = (e) => e.total && setUploadProgress(Math.round((e.loaded / e.total) * 100))
    const { error } = await dispatch(submitReclamation(
      { ...data, datumPodani: getTodaysDateString() }, files, sessionId, onUploadProgress
    ))

    if (!error) {
      history.push({
        pathname: '/submitted',
        state: { submitted: true },
      });
      return
    }

    // Formulář zůstává vyplněný, klient může po úpravě příloh odeslat znovu
    setIsSubmitting(false)
    setSubmitError(error)
  };

  const handleSubmitPopup = (data) => {
    setDialog({
      id: 'handleSubmitPopup',
      show: true,
      onProceed: () => handleSubmitForm(data),
      onCancel: dialogReset,
      iconPath: icoQuestion,
      title: `Potvrzení reklamace?`,
      text: 'Prosím ujištete se, zda jste vyplnili správné údaje a závada je dostatečně popsána. Urychlí to vyřízení celé reklamace.',
      confirmText: 'Odeslat reklamaci',
      cancelText: 'Znovu zkontrolovat',
    })
  };

  const dialogReset = () => setDialog(null);

  const chooseFile = () => {
    fileInput.current.click();
  };

  const handleFileChange = async (e) => {
    e.preventDefault();
    const selectedFiles = Array.from(e.target.files);

    setIsPreparingFiles(true)
    const { accepted, errors } = await prepareAttachments(selectedFiles, files)
    setIsPreparingFiles(false)

    setFiles([...files, ...accepted])
    setFileErrors(errors)
    setSubmitError(null)
  };

  const handleRemove = (index) => {
    setFiles(files.filter((_, i) => i !== index))
    setFileErrors([])
    setSubmitError(null)
  };

  return (
    <React.Fragment>
      {isSubmitting && <SubmittingPage progress={files.length ? uploadProgress : null} />}
      {/* Formulář se při odesílání jen skryje, aby při chybě zůstaly vyplněné údaje */}
      <div style={isSubmitting ? { display: 'none' } : undefined}>
        <FormWrapper class='form-wrapper'>
          <Form
            onSubmit={handleSubmitPopup}
            render={({ handleSubmit, values }) => {
              const secondContact = values['secondContact']

              return (
                <form onSubmit={handleSubmit}>
                  <Title title={'Reklamační formulář'} />
                  <OwnerContactInformationSection secondContact={secondContact} />
                  <ApartmentInformationSection
                    residence={residence}
                    residenceNames={residenceNamesItems}
                  />
                  <DefectInformationSection
                    categoriesNames={categoriesItems}
                    files={files}
                    fileErrors={fileErrors}
                    isPreparingFiles={isPreparingFiles}
                    submitError={submitError}
                    fileInput={fileInput}
                    handleFileChange={handleFileChange}
                    handleChooseFileClick={chooseFile}
                    handleRemoveFileClick={handleRemove}
                  />
                </form>
              )
            }} />
        </FormWrapper>
      </div>
      {dialog && dialog.show && <ConfirmDialog {...dialog} />}
    </React.Fragment>
  );
}

export default ReclamationPage
